import { useEffect, useState } from 'react';

/**
 * Tracks the on-screen position of an anchor element and returns a
 * `position: fixed` style object suitable for a portal-rendered popover.
 *
 * Renders at document.body to escape any ancestor with overflow:hidden,
 * transform, or a lower stacking context. Recomputes on scroll/resize so
 * the popover follows the anchor while open.
 *
 * Responsive behavior:
 *   - Width clamps to viewport (preferred width, capped at viewport - 16px gutter)
 *   - Falls back to "above anchor" placement when the bottom doesn't fit
 *   - max-height: bounds whichever direction has more room (scrolls inside)
 *
 * @param {object} anchorRef — ref to the trigger element
 * @param {boolean} open     — only computes when true
 * @param {object} options
 *   - placement: 'bottom-right' | 'bottom-left' | 'bottom-center' (default 'bottom-right')
 *   - gap: px between anchor and popover (default 6)
 *   - preferredWidth: desired width in px (default 320). Will shrink on narrow screens.
 */
export default function usePopoverAnchor(anchorRef, open, options = {}) {
    const { placement = 'bottom-right', gap = 6, preferredWidth = 320 } = options;
    const [style, setStyle] = useState({});

    useEffect(() => {
        if (!open || !anchorRef?.current) return;

        const computePosition = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;

            const vpW = window.innerWidth;
            const vpH = window.innerHeight;
            const gutter = 8;

            // Responsive width — never exceed viewport minus gutter
            const width = Math.min(preferredWidth, vpW - gutter * 2);

            // Horizontal alignment relative to the anchor
            let left;
            if (placement === 'bottom-left')        left = rect.left;
            else if (placement === 'bottom-center') left = rect.left + rect.width / 2 - width / 2;
            else                                    left = rect.right - width;
            left = Math.max(gutter, Math.min(left, vpW - width - gutter));

            // Vertical placement — bottom unless the anchor is too close to the
            // viewport's bottom edge, in which case flip above.
            const spaceBelow = vpH - rect.bottom - gap - gutter;
            const spaceAbove = rect.top - gap - gutter;
            const flipAbove = spaceBelow < 200 && spaceAbove > spaceBelow;

            const maxHeight = Math.max(160, flipAbove ? spaceAbove : spaceBelow);
            const top = flipAbove ? rect.top - gap : rect.bottom + gap;
            const transform = flipAbove ? 'translateY(-100%)' : 'none';

            setStyle({
                position: 'fixed',
                top,
                left,
                width,
                maxHeight,
                overflowY: 'auto',
                transform,
                zIndex: 60,  // above the sidebar (z-50)
            });
        };

        computePosition();
        window.addEventListener('scroll', computePosition, true);
        window.addEventListener('resize', computePosition);
        return () => {
            window.removeEventListener('scroll', computePosition, true);
            window.removeEventListener('resize', computePosition);
        };
    }, [open, anchorRef, placement, gap, preferredWidth]);

    return style;
}
